import { Icon } from '@buggy/shared'
import { Select, SelectProps } from 'antd'
import { FunctionComponent } from 'react'
import styled, { useTheme } from 'styled-components'
import BasicSelect from '@/components/common/antd/basic-select'
import { NoData } from '@/components/kol/detail/no-data'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  convertPlatformShortcode,
  PlatformShortcode,
} from '@/utils/convert-platform'

type PlatformSelectorProps = {
  showIcon?: boolean
  availablePlatforms?: PlatformShortcode[]
  iconSize?: number
  isDemo?: boolean
} & Pick<SelectProps, 'size' | 'allowClear' | 'dropdownStyle'> &
  (
    | {
        multiple?: false
        hasAllOption?: boolean
        value?: PlatformShortcode
        onChange?: (platform: PlatformShortcode) => void
      }
    | {
        multiple: true
        hasAllOption?: false
        value?: PlatformShortcode[]
        onChange?: (platform: PlatformShortcode[]) => void
      }
  )

const PlatformSelector: FunctionComponent<PlatformSelectorProps> = ({
  size = 'small',
  hasAllOption = false,
  showIcon = false,
  value,
  onChange,
  availablePlatforms = [
    PlatformShortcode.Instagram,
    PlatformShortcode.Facebook,
    PlatformShortcode.YouTube,
  ],
  multiple = false,
  iconSize = 14,
  isDemo,
  ...otherProps
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  return (
    <StyledPlatformSelector
      {...otherProps}
      getPopupContainer={(triggerNode): HTMLElement => triggerNode}
      mode={multiple ? 'multiple' : undefined}
      notFoundContent={<NoData />}
      placeholder={formatMessage({
        id: 'irm:community_data_header_please_select',
      })}
      size={size}
      value={value}
      onChange={onChange}
    >
      {hasAllOption && (
        <Select.Option disabled={isDemo} key={PlatformShortcode.All}>
          <IconWrapper>
            <Icon
              color={theme.colors.brand.primary}
              fontSize={iconSize}
              name='grid'
            />
          </IconWrapper>
          <OptionLabel>
            {formatMessage({ id: 'project:platform_option_all' })}
          </OptionLabel>
        </Select.Option>
      )}

      {availablePlatforms.map((platformShortcode) => {
        const platform = convertPlatformShortcode(platformShortcode)
        return (
          <Select.Option
            disabled={isDemo}
            key={platformShortcode}
            value={platformShortcode}
          >
            {showIcon && platform.sharedIconName && (
              <IconWrapper>
                <Icon fontSize={iconSize} name={platform.sharedIconName} />
              </IconWrapper>
            )}
            <OptionLabel>{platform.title}</OptionLabel>
          </Select.Option>
        )
      })}
    </StyledPlatformSelector>
  )
}

const OptionLabel = styled.div`
  width: 65px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`

export const StyledPlatformSelector = styled(BasicSelect)`
  width: 150px;

  .ant-select-selection-item,
  .ant-select-item-option-content {
    display: flex;
    gap: 4px;
  }

  .ant-select-item-option-disabled {
    ${OptionLabel} {
      color: unset;
    }
  }
`

export default PlatformSelector
